import React, { useState, useEffect } from 'react';
import { Briefcase, Repeat, DollarSign, UserCheck, CreditCard, Layers, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { adminApi, STATE_LABELS } from '@/components/admin/adminApi';

const Stat = ({ icon: Icon, label, value, tone, testid, sub }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-4.5 h-4.5" /></div>
    </div>
    <p className="mt-3 text-2xl font-extrabold text-white leading-none">{value}</p>
    <p className="mt-1 text-xs text-slate-400">{label}</p>
    {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
  </div>
);

/* PayMongo readiness & environment — factual, reported by GET /api/billing/readiness.
   Capability (recurring) is never inferred from key presence. */
const PaymentReadiness = () => {
  const [r, setR] = useState(null);
  useEffect(() => { adminApi.readiness().then(setR).catch(() => {}); }, []);
  if (!r) return null;
  const configured = r.configured;
  const modeLabel = { live: 'LIVE', test: 'TEST', not_configured: 'NOT CONFIGURED', unknown: 'UNVERIFIED' }[r.mode] || 'UNVERIFIED';
  const Icon = configured ? ShieldCheck : ShieldAlert;
  const tone = configured ? 'text-green-400' : 'text-amber-400';
  const Chip = ({ label, value, ok }) => (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-700/60 text-[11px] text-slate-300">
      {label}<span className={`font-bold ${ok === true ? 'text-green-400' : ok === false ? 'text-amber-400' : 'text-white'}`}>{value}</span>
    </span>
  );
  return (
    <div className="mb-4 bg-slate-800 border border-slate-700 rounded-xl p-4" data-testid="payment-readiness">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${tone}`} />
        <p className="text-xs font-bold text-white uppercase tracking-wider">PayMongo Readiness</p>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${configured ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-400'}`} data-testid="readiness-mode">{modeLabel}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Chip label="Webhook verify " value={r.webhook_verification ? 'on' : 'off'} ok={r.webhook_verification} />
        <Chip label="Auto-charge " value={r.auto_charge_enabled ? 'on' : 'off (safe)'} ok={!r.auto_charge_enabled} />
        <Chip label="Recurring " value={r.recurring_status} />
        <Chip label="Charge " value={r.charge_currency} />
        <Chip label="Commercial " value={r.commercial_currency} />
        <Chip label="FX " value={`₱${r.fx_rate}/USD (${r.fx_version})`} />
        <Chip label="Methods " value={(r.payment_methods || []).join(', ')} />
      </div>
      {!configured && (
        <p className="mt-2 text-[11px] text-amber-400/90">Payments run in simulation mode here (no keys). Online payment buttons stay hidden for clients until configured. Native recurring requires separate PayMongo activation.</p>
      )}
    </div>
  );
};

export const DaasMetrics = () => {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminApi.metrics().then(setM).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-brand-blue animate-spin" /></div>;
  if (!m) return null;

  const pipeline = m.projects_pipeline || {};
  const pipelineEntries = Object.entries(pipeline).sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="daas-metrics" className="mb-8">
      <PaymentReadiness />
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">Subscriptions &amp; Projects</h3>
        <span className="text-[11px] text-slate-500">MRR counts active subscriptions only — excludes legacy orders &amp; demo payments</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={DollarSign} label="Recurring revenue (MRR)" value={`$${(m.mrr_usd || 0).toLocaleString()}`} tone="bg-brand-orange/15 text-brand-orange" testid="metric-mrr" sub={`${m.subscriptions_active} active subs`} />
        <Stat icon={Briefcase} label="Active projects" value={m.projects_active} tone="bg-brand-blue/15 text-brand-blue" testid="metric-active-projects" sub={`${m.projects_total} total`} />
        <Stat icon={Repeat} label="Active subscriptions" value={m.subscriptions_active} tone="bg-green-500/15 text-green-400" testid="metric-active-subs" />
        <Stat icon={UserCheck} label="Qualified leads" value={m.leads_qualified} tone="bg-purple-500/15 text-purple-400" testid="metric-qualified-leads" sub={`${m.leads_total} total leads`} />
        <Stat icon={CreditCard} label="Pending payments" value={m.pending_payment_projects} tone="bg-amber-500/15 text-amber-400" testid="metric-pending-payments" sub={`${m.unpaid_invoices} unpaid invoices`} />
        <Stat icon={Layers} label="Legacy projects" value={m.legacy_projects} tone="bg-slate-600/40 text-slate-300" testid="metric-legacy-projects" />
      </div>

      {pipelineEntries.length > 0 && (
        <div className="mt-4 bg-slate-800 border border-slate-700 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Project pipeline</p>
          <div className="flex flex-wrap gap-2">
            {pipelineEntries.map(([state, count]) => (
              <span key={state} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700/60 text-xs text-slate-200">
                {STATE_LABELS[state] || state}
                <span className="font-bold text-white">{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
